import {AwAppShell, AwButton, AwCheckbox, AwEmptyState, AwErrorState, AwLoadingState, AwPageHeader, AwPanel, AwProjectCard} from '@ascension-tracker/ascension-ui';
import {api} from '../api.js';
import {useAsync} from '../hooks/useAsync.js';
import {useMyTasks, useToggleMyTaskComplete} from '../hooks/useTasks.js';

type ProjectSummary = {id: string; displayName: string; projectKey: string; description: string; lifecyclePath: 'standard' | 'early_access'; state: string; currentReleaseId: string | null};

function todayKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function Home() {
  const projects = useAsync(() => api<{items: ProjectSummary[]}>('/projects'), []);
  const tasks = useMyTasks();
  const toggleComplete = useToggleMyTaskComplete();
  const today = todayKey();
  const dueSoon = (tasks.data ?? [])
    .filter(task => task.dueOn && task.dueOn.slice(0, 10) <= today)
    .slice(0, 5);

  return (
    <AwAppShell>
      <AwPageHeader eyebrow="Ascension: Warlords Tracker" title="Home">
        <p>A quick look at what needs your attention and where your projects stand.</p>
      </AwPageHeader>

      <AwPanel className="home-panel">
        <header className="home-panel__header"><h2>Due today or overdue</h2><a href="/my-tasks">View all my tasks →</a></header>
        {tasks.error && <AwErrorState>{(tasks.error as Error).message}</AwErrorState>}
        {!tasks.data ? <AwLoadingState/> : dueSoon.length === 0 ? (
          <p className="task-detail__muted">Nothing due today or overdue. You're caught up.</p>
        ) : (
          <div className="my-tasks__bucket">
            {dueSoon.map(task => (
              <div key={task.id} className="my-tasks__row">
                <AwCheckbox id={`home-task-complete-${task.id}`} label="" checked={Boolean(task.completedAt)} onChange={() => toggleComplete.mutate(task)}/>
                <a className="my-tasks__title" href={`/projects/${task.projectKey}/tasks`}>{task.title}</a>
                <span className="my-tasks__project">{task.projectName}</span>
              </div>
            ))}
          </div>
        )}
      </AwPanel>

      <section className="home-projects">
        <header className="home-panel__header"><h2>Your projects</h2><AwButton variant="secondary" onClick={() => location.assign('/projects')}>All Projects</AwButton></header>
        {error(projects.error)}
        {!projects.data ? <AwLoadingState/> : projects.data.items.length === 0 ? (
          <AwPanel><AwEmptyState title="No projects yet"><p>Create your first project to get started.</p></AwEmptyState></AwPanel>
        ) : (
          <div className="project-grid">
            {projects.data.items.slice(0, 6).map(project => (
              <AwProjectCard key={project.id} name={project.displayName} projectKey={project.projectKey} description={project.description} href={`/projects/${project.projectKey}/overview`}/>
            ))}
          </div>
        )}
      </section>
    </AwAppShell>
  );
}

function error(message: string | undefined) {
  return message ? <AwErrorState>{message}</AwErrorState> : null;
}
